<template>
  <div class="vcl-tab-menu" :style="tabStyles">
    <ul ref="tabs" class="vcl-tab-menu__group">
      <template v-for="(tab, i) in tabs">
        <li
          :key="tab.value"
          :tabindex="isSelected(tab) ? 0 : -1"
          class="vcl-tab-menu__tab"
          :class="[{'is-selected': isSelected(tab) }, { 'is-disabled': tab.disabled }]"
          @click="!tab.disabled && select(tab)"
          @keydown="navigate($event, i)"
        >
          <slot :name="tab.value" :selected="isSelected(tab)" :disabled="tab.disabled">
            {{ tab.label }}
          </slot>
        </li>
      </template>
    </ul>

    <template v-for="tab in tabs">
      <slot v-if="isSelected(tab)" :name="tab.value+'-content'" />
    </template>
  </div>
</template>

<script>
export default {
  name: 'VpTabMenu',
  props: {
    value: {
      type: String,
      default: null,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  emits: ['input'],
  data() {
    return {
      selected: null,
    };
  },
  computed: {
    tabStyles() {
      const ALIGNMENT_OPTIONS = {
        left: 'flex-start',
        right: 'flex-end',
        center: 'center',
        'space-between': 'space-between',
        'space-around': 'space-around',
      };

      const styles = {
        '--tab-menu-align': ALIGNMENT_OPTIONS[this.styles.align] || 'flex-start',
        '--tab-menu-height': this.styles.menuHeight || '45px',
        '--tab-menu-padding': this.styles.menuPadding || '0px',
        '--tab-menu-border-left': this.styles.menuBorderLeft || '0',
        '--tab-menu-border-top': this.styles.menuBorderTop || '0',
        '--tab-menu-border-right': this.styles.menuBorderRight || '0',
        '--tab-menu-border-bottom': this.styles.menuBorderBottom || '0',
        '--tab-margin': this.styles.tabMargin || '0 12px',
        '--tab-font-size': this.styles.fontSize || '16px',
        '--tab-active-color': this.styles.activeColor || '#1B3664',
        '--tab-inactive-color': this.styles.inactiveColor || '#A6B1C3',
        '--tab-active-underline-color': this.styles.activeUnderlineColor || '#F58220',
        '--tab-background-color': this.styles.backgroundColor || '#F4F7F8',
        '--tab-menu-box-shadow': this.styles.menuBoxShadow || 'inset 0px -1px 2px rgba(0, 0, 0, 0.05)',
      };
      return styles;
    },
    currentPath() {
      return this.$route.path || window.location.href || null;
    },
    currentHash() {
      return this.$route.hash || null;
    },
    currentTabComponent() {
      const tab = this.tabs.find(({ value }) => value === this.selected);

      return tab.component;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.selected = value;
        const matched = this.tabs.find(({ path }) => path === this.currentPath || path === this.currentHash);
        if (matched) {
          this.selected = matched.value;
        }
      }
    },
    selected: {
      immediate: true,
      handler(value) {
        this.$emit('input', value);
      }
    }
  },
  methods: {
    isSelected(tab) {
      return tab.value === (this.selected || this.value);
    },
    select(tab) {
      this.selected = tab.value;
      if (tab.path && tab.path !== this.currentPath) {
        this.$router.push({ path: tab.path });
      }
    },
    navigate(evt, index) {
      if (evt.key === 'ArrowLeft') {
        const prev = index - 1;
        if (prev >= 0) {
          const { value, disabled } = this.tabs[prev];
          if (!disabled) {
            this.selected = value;
          }
          this.$refs.tabs.children[prev].focus();
        }
      } else if (evt.key === 'ArrowRight') {
        const next = index + 1;
        if (next <= this.tabs.length) {
          const { value, disabled } = this.tabs[next];
          if (!disabled) {
            this.selected = value;
          }

          this.$refs.tabs.children[next].focus();
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.vcl-tab-menu {
  $self: &;

  .vcl-tab-menu__group {
    margin: 0;
    border-left: var(--tab-menu-border-left);
    border-top: var(--tab-menu-border-top);
    border-right: var(--tab-menu-border-right);
    border-bottom: var(--tab-menu-border-bottom);
    padding: var(--tab-menu-padding);
    display: flex;
    flex-direction: row;
    justify-content: var(--tab-menu-align);
    list-style: none;
    width: auto;
    background-color: var(--tab-background-color);
    box-shadow: var(--tab-menu-box-shadow);
    #{ $self }__tab {
      display: inline-block;
      text-decoration: none;
      line-height: var(--tab-menu-height);
      padding: 0 2px;
      margin: var(--tab-margin);
      user-select: none;
      cursor: pointer;
      font-size: var(--tab-font-size);
      color: var(--tab-inactive-color);
       &.is-selected {
        color: var(--tab-active-color);
        background: linear-gradient(0deg, var(--tab-active-underline-color) 4px, transparent 0px);
      }
      &.is-disabled {
        cursor: not-allowed;
      }
    }
}
}
</style>
